import { Fragments, Mutations } from '@shared/api';
import { getSharedApolloClient } from '@shared/api/client';
import { MBChannelTypeUtil } from '@shared/scenes/chat/thread/util/channelType';
import { getStore } from '@shared/store';
import { API } from '@shared/types/api';
import { MBAnalytics } from '@shared/util/analytics';
import { notNil } from '@shared/util/notNil';

export async function toggleReaction(
  message: API.ChatMessageFragment,
  channelSlug: string,
  reactionType: API.ReactionType,
  add: boolean,
) {
  const store = getStore();
  const myId = store.getState().appState.currentUserId;
  const apolloClient = getSharedApolloClient();
  const reactions = message.meta.reactions.filter(notNil);

  // Check if there is already a reaction by us. If we've already
  // reacted/not reacted, we don't need to send a new request.
  const hasReacted = reactions.some(
    reaction => reaction.type === reactionType && reaction.user.id === myId,
  );
  if (hasReacted === add) {
    return;
  }

  let optimisticReactions: API.ChatMessageFragment['meta']['reactions'];

  if (add) {
    // Get our own user to we can create a proper optimistic response
    const me = apolloClient.readFragment<API.ShortUserFragment>({
      id: `User${myId}`,
      fragment: Fragments.user.short,
    });

    // Note(fant): we do not push to message's reactions, since that would
    // make Apollo's cache confused (optimistic response wouldn't be rendered)
    optimisticReactions = [
      ...reactions,
      { __typename: 'Reaction', type: reactionType, user: me } as API.ReactionFragment,
    ];

    MBAnalytics.track('Message::AddReaction', { reactionType, messageId: message.id });
  } else {
    optimisticReactions = reactions.filter(
      reaction => !(reaction.user.id === myId && reaction.type === reactionType),
    );

    MBAnalytics.track('Message::RemoveReaction', { reactionType, messageId: message.id });
  }

  // Instead of using apolloClient.mutate's optimisticResponse, we update
  // the Apollo store directly. The reason we do this is due to this bug:
  // If we set optimistic UI (eg { ...message.meta, reactions: optimisticReactions }),
  // Apollo would in some way modify all messages (maybe due to that the
  // GraphQL Reactions type doesn't have an ID and therefore can't be cached?).
  // So MBChatThread would re-render every single message, causing extreme lag.
  // By just hand-picking the correct meta, we avoid this. Only the message
  // is re-rendered (which is expected).
  //
  // One known "bug" with this is the following:
  // 1. Enter chat with high latency
  // 2. Quickly add a reaction
  // 3. The chat thread request completes
  // 4. The new data overrides our "optimistic" data from below and reverts
  // 5. When the reaction mutation is completed, the UI is updated anew

  // 1. get the current message's meta fragment
  const meta = apolloClient.readFragment<API.ChatMessageMetaFragment>({
    id: `Meta${message.id}`,
    fragment: Fragments.chat.messageMeta,
    fragmentName: 'ChatMessageMeta',
  });

  // 2. update the reactions
  if (!!meta) {
    apolloClient.writeFragment({
      id: `Meta${meta.id}`,
      fragment: Fragments.chat.messageMeta,
      fragmentName: 'ChatMessageMeta',
      data: { ...meta, reactions: optimisticReactions },
    });
  }

  // 3. do the actual mutation
  if (MBChannelTypeUtil.isThread(channelSlug)) {
    // TODO(nf): error is returned if calling below https://app.shortcut.com/coast/story/30746/threads-fix-reaction-mutation-in-chat-threads
    console.error('TODO(fant): toggleReaction');
  } else {
    await apolloClient.mutate({
      mutation: Mutations.chat.updateMessageReaction,
      variables: {
        reactionType,
        add,
        messageId: message.id,
      } as API.UpdateMessageReactionMutationVariables,
    });
  }
}
